import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { motion } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import { useAppSelector } from '../redux/hooks';
import BasicMenuItemCard from './BasicMenuItemCard';
import ClassicMenuItemCard from './ClassicMenuItemCard';
import PrimeMenuItemCard from './PrimeMenuItemCard';
import MenuItemCard from './MenuItemCard';

// ============================================================================
// CONSTANTS
// ============================================================================

const PRIMARY_COLOR = '#EE4446';

const MENU_STYLES = [
  'BasicMenuItems',
  'ClassicMenuItems',
  'ModernMenuItems',
  'PrimeMenuItems',
] as const;

// ============================================================================
// TYPES
// ============================================================================

interface MockMenuItem {
  pk_id: string;
  name: string;
  description: string;
  price: string;
  image: string;
  category: string;
  level1_category: string;
  level2_category: string;
  is_enabled: boolean;
  is_item_verified: boolean;
  inventory_status: boolean;
  food_type: string;
  is_spice_applicable: string;
  modifiers_list: any[];
}

interface MenuCardPreviewProps {
  styleType: string;
}

export interface MenuStyleSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectStyle: (styleType: string) => void;
  isLoading?: boolean;
}

// ============================================================================
// MOCK DATA
// ============================================================================

const mockMenuItems: MockMenuItem[] = [
  {
    pk_id: 'f03c1c36-597d-4afc-ab37-3700b0f70d35',
    name: 'Classic Margherita',
    description: 'A Margherita pizza is a classic Italian dish known for its simple yet flavorful ingredients.',
    price: '11',
    image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1754577442034-generated-image-1754577438889-2.png',
    category: 'Entrees',
    level1_category: 'Entrees',
    level2_category: 'Vegetable Based Mains',
    is_enabled: true,
    is_item_verified: true,
    inventory_status: true,
    food_type: 'Veg',
    is_spice_applicable: 'Yes',
    modifiers_list: [],
  },
  {
    pk_id: 'b3d1ed75-e9df-45d0-a393-fbc1109c0aaa',
    name: 'Veggie Supreme',
    description: 'Veggie Supreme is a hearty vegetarian pizza topped with a variety of fresh vegetables and melted cheese.',
    price: '13',
    image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1749534377844-generated-image-1749534377844-1.png',
    category: 'Starters & Small Plates',
    level1_category: 'Starters & Small Plates',
    level2_category: 'Breads & Flatbreads',
    is_enabled: true,
    is_item_verified: true,
    inventory_status: true,
    food_type: 'Veg',
    is_spice_applicable: 'Yes',
    modifiers_list: [],
  },
  {
    pk_id: '3874684b-bba8-4b91-b865-91b34e951477',
    name: 'Chicken Tikka Pizza',
    description: 'Chicken Tikka Pizza features tender chicken tikka pieces atop a crispy crust, topped with cheese and spices.',
    price: '13.49',
    image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1749534429912-generated-image-1749534429081-3.png',
    category: 'Starters & Small Plates',
    level1_category: 'Starters & Small Plates',
    level2_category: 'Appetizers / Starters',
    is_enabled: true,
    is_item_verified: true,
    inventory_status: true,
    food_type: 'non_veg',
    is_spice_applicable: 'No',
    modifiers_list: [],
  },
  {
    pk_id: '59d63f4e-d5e7-4dce-906b-4645a2e44a1a',
    name: 'BBQ Chicken Feast Pizza',
    description: 'BBQ Chicken Feast Pizza features a crispy crust topped with smoky barbecue sauce, tender chicken, cheese, and flavorful toppings.',
    price: '13.99',
    image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1749534604205-generated-image-1749534602683-3.png',
    category: 'Entrees',
    level1_category: 'Entrees',
    level2_category: 'Pizzas & Flatbreads',
    is_enabled: true,
    is_item_verified: true,
    inventory_status: true,
    food_type: 'non_veg',
    is_spice_applicable: 'yes',
    modifiers_list: [],
  },
];

// ============================================================================
// MENU CARD PREVIEW COMPONENT
// ============================================================================

const MenuCardPreview = memo(function MenuCardPreview({ 
  styleType 
}: MenuCardPreviewProps) {
  // Memoize shared props to prevent recreating objects on each render
  const sharedCardProps = useMemo(() => ({
    onItemClick: () => {},
    onAddToCart: () => {},
    showAddToCart: true,
  }), []);

  // Render appropriate card component based on style type
  const renderCards = () => {
    switch (styleType) {
      case 'BasicMenuItems':
        return mockMenuItems.map((item) => (
          <BasicMenuItemCard
            key={item.pk_id}
            item={item as any}
            {...sharedCardProps}
          />
        ));
      
      case 'ClassicMenuItems':
        return mockMenuItems.map((item) => (
          <ClassicMenuItemCard
            key={item.pk_id}
            item={item as any}
            {...sharedCardProps}
          />
        ));
      
      case 'ModernMenuItems':
        return mockMenuItems.map((item) => (
          <MenuItemCard
            key={item.pk_id}
            item={item as any}
            {...sharedCardProps}
          />
        ));
      
      case 'PrimeMenuItems':
        return mockMenuItems.map((item) => (
          <PrimeMenuItemCard
            key={item.pk_id}
            item={item as any}
            {...sharedCardProps}
          />
        ));
      
      default:
        return (
          <div className="flex h-32 w-full items-center justify-center rounded border border-gray-200 bg-gray-50">
            <p className="text-gray-500">Preview not available</p>
          </div>
        );
    }
  };

  const cards = renderCards();
  const cardArray = Array.isArray(cards) ? cards : [cards];
  
  // Memoize layout class to avoid recalculation
  const layoutClass = useMemo(
    () => styleType === 'PrimeMenuItems' 
      ? 'pointer-events-none select-none grid grid-cols-2 gap-4'
      : 'pointer-events-none select-none grid grid-cols-4 gap-4',
    [styleType]
  );

  return (
    <div className={layoutClass}>
      {cardArray.map((card, index) => (
        <div key={index}>
          {card}
        </div>
      ))}
    </div>
  );
});

// ============================================================================
// MAIN MODAL COMPONENT
// ============================================================================

const MenuStyleSelectionModal = memo(function MenuStyleSelectionModal({
  isOpen,
  onClose,
  onSelectStyle,
  isLoading = false,
}: MenuStyleSelectionModalProps) {
  // ============================================================================
  // STATE & REFS
  // ============================================================================
  
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // ============================================================================
  // REDUX SELECTORS
  // ============================================================================
  
  const { data: siteContentData } = useAppSelector(state => state.siteContent);
  const currentMenuWidget = siteContentData?.menuWidgetTypes?.[0] || 'BasicMenuItems';

  // ============================================================================
  // EMBLA CAROUSEL
  // ============================================================================
  
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'center',
    skipSnaps: false,
    dragFree: false,
    duration: 20, // Smooth animation duration (lower = faster, higher = slower)
  });

  // ============================================================================
  // CALLBACKS
  // ============================================================================
  
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') scrollPrev();
      if (e.key === 'ArrowRight') scrollNext();
    },
    [onClose, scrollPrev, scrollNext]
  );
  
  const handleSave = useCallback(() => {
    if (selectedStyle) {
      onSelectStyle(selectedStyle);
      // Don't close here - let MenuPage useEffect close after API completes
    }
  }, [selectedStyle, onSelectStyle]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Auto-select current style when modal opens
  useEffect(() => {
    if (isOpen && currentMenuWidget) {
      setSelectedStyle(currentMenuWidget);
      const index = MENU_STYLES.indexOf(currentMenuWidget as any);
      if (index !== -1) {
        setCurrentIndex(index);
        if (emblaApi) {
          emblaApi.scrollTo(index, false);
        }
      }
    }
  }, [isOpen, currentMenuWidget, emblaApi]);

  // Sync carousel index with Embla state and auto-select style
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const newIndex = emblaApi.selectedScrollSnap();
      setCurrentIndex(newIndex);
      // Auto-select style based on active tab
      setSelectedStyle(MENU_STYLES[newIndex]);
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  // Keyboard navigation
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="menu-style-modal-title"
    >
      <div className="flex w-auto flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div>
            <h2 id="menu-style-modal-title" className="text-xl font-semibold text-gray-900">
              Select a Menu Card Style
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Preview and choose a layout for your menu items. Use the arrow keys or navigation buttons to explore different styles.
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-gray-100"
            aria-label="Close modal"
          >
            <FiX className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 pb-4">
          {/* Tab Navigation with Animated Underline */}
          <div className="relative mb-3 border-b border-gray-200">
            <div className="flex justify-start gap-1">
              {MENU_STYLES.map((styleType, index) => {
                const isActive = currentIndex === index;
                return (
                  <button
                    key={styleType}
                    onClick={() => emblaApi?.scrollTo(index)}
                    className={`
                      relative px-6 py-3 font-medium transition-colors duration-200
                      ${isActive ? 'text-red-600' : 'text-gray-600 hover:text-gray-900'}
                    `}
                  >
                    {styleType.replace('MenuItems', '')}

                    {/* Animated sliding underline */}
                    {isActive && (
                      <motion.div
                        layoutId="tab-underline"
                        className="absolute bottom-0 left-0 right-0 h-[3px] rounded-full"
                        style={{ backgroundColor: PRIMARY_COLOR }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 30,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Embla Carousel */}
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {MENU_STYLES.map((styleType) => (
                <div key={styleType} className="min-w-0 flex-[0_0_100%]">
                  <div
                    className={`rounded-lg border-2 p-4 transition-all ${
                      selectedStyle === styleType
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <MenuCardPreview styleType={styleType} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 p-6">
          <button
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-6 py-2 font-medium text-gray-700 transition hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedStyle || isLoading}
            className={`rounded-lg px-6 py-2 font-medium transition ${
              selectedStyle && !isLoading
                ? 'text-white hover:opacity-90'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
            style={selectedStyle && !isLoading ? { backgroundColor: PRIMARY_COLOR } : undefined}
          >
            {isLoading ? 'Selecting...' : `Select ${selectedStyle?.replace('MenuItems', '') || 'Style'}`}
          </button>
        </div>
        
      </div>
    </div>
  );
});

MenuStyleSelectionModal.displayName = 'MenuStyleSelectionModal';
export default MenuStyleSelectionModal;
